import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  business_id: string | null;
  branch_id: string | null;
  is_active: boolean | null;
  phone: string | null;
  avatar_url: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  isLoading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single();
    
    if (!error && data) {
      setProfile(data);
    }
  };

  const refreshProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const safeSetProfile = async (userId: string) => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .single();
        if (isMounted && !error && data) {
          setProfile(data);
        }
      } catch {
        // Profile fetch failed silently — don't block loading
      }
    };

    // ONGOING auth changes — does NOT control loading state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // setTimeout avoids Supabase deadlock inside the callback
          setTimeout(() => safeSetProfile(session.user.id), 0);
        } else {
          setProfile(null);
        }
      }
    );

    // INITIAL load — with hard timeouts to prevent infinite spinner on published URL
    const initializeAuth = async () => {
      const timeoutPromise = new Promise<void>((resolve) =>
        setTimeout(resolve, 8000) // 8-second absolute max
      );

      const authPromise = (async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!isMounted) return;
          setSession(session);
          setUser(session?.user ?? null);
          if (session?.user) {
            // Profile fetch with its own 4-second timeout
            await Promise.race([
              safeSetProfile(session.user.id),
              new Promise<void>(resolve => setTimeout(resolve, 4000))
            ]);
          }
        } catch {
          // Auth initialization failed — clear state and show login
        }
      })();

      await Promise.race([authPromise, timeoutPromise]);
      if (isMounted) setLoading(false);
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    // Remove session token on logout
    const sessionToken = localStorage.getItem('pos_session_token');
    if (sessionToken) {
      try {
        await supabase.functions.invoke('manage-session', {
          body: { action: 'remove', sessionToken },
        });
      } catch (err) {
        console.error('Session removal error:', err);
      }
      localStorage.removeItem('pos_session_token');
    }
    setProfile(null);
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      profile, 
      loading, 
      isLoading: loading,
      signUp, 
      signIn, 
      signOut,
      refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
